/**
 * 旅行的意义（Meaning）长期篇章数据
 *
 * 目标：让“每个月运营”尽量只改数据、不改组件逻辑。
 * 后续扩展月份时，只需要新增 monthId 对应的配置对象即可。
 */

import { createRemoteImageAsset, type ImageAsset } from '../types/imageAsset';

export type MeaningMonthStage = 'recruiting' | 'inProgress' | 'developing' | 'published';

export type MeaningLangText = {
  zh: string;
  en: string;
};

export interface MeaningVideoSelfHosted {
  src: string; // mp4/webm
  poster?: string; // 可选封面
}

export interface MeaningVideoEmbed {
  embedUrl: string; // 已处理好的 iframe 地址
  title?: string;
}

export type MeaningVideoSource =
  | { kind: 'selfHosted'; selfHosted: MeaningVideoSelfHosted }
  | { kind: 'embed'; embed: MeaningVideoEmbed };

export type MeaningMomentType = 'photo' | 'video' | 'text';

export interface MeaningMomentBase {
  id: string;
  type: MeaningMomentType;
  tags: string[]; // 用于 Moments Mosaic 筛选
  source?: MeaningLangText; // 轻量来源标记：如“4月 · 广州”
}

export interface MeaningPhotoMoment extends MeaningMomentBase {
  type: 'photo';
  photo: ImageAsset;
  zhCaption?: string;
  enCaption?: string;
}

export interface MeaningVideoMoment extends MeaningMomentBase {
  type: 'video';
  // 支持混合来源：自托管/平台嵌入
  videoSources: MeaningVideoSource[];
}

export interface MeaningTextMoment extends MeaningMomentBase {
  type: 'text';
  zhText: string;
  enText: string;
}

export type MeaningMoment = MeaningPhotoMoment | MeaningVideoMoment | MeaningTextMoment;

export interface MeaningFilmPack {
  id: string;
  title: MeaningLangText;
  photos: ImageAsset[];
}

export interface MeaningShowcaseVideo {
  id: string;
  title: MeaningLangText;
  sources: MeaningVideoSource[]; // 混排：自托管 + 嵌入
}

export interface MeaningShowcaseMessage {
  id: string;
  content: MeaningLangText;
}

export interface MeaningHero {
  heroVideoSources?: MeaningVideoSource[]; // 可选：没有视频也能正常渲染
  posterImage?: ImageAsset; // 可选：用于没有视频时的封面
}

export type MeaningMemberStatus = 'preparing' | 'traveling' | 'returned' | 'published';

export interface MeaningMember {
  id: string;
  name: string;
  avatar?: string;
  city: string;
  keyword: MeaningLangText;
  tagline: MeaningLangText;
  status: MeaningMemberStatus;
  submissions: MeaningMoment[];
}

export interface MeaningRecruitContent {
  title: MeaningLangText;
  subtitle: MeaningLangText;
  narrativeParagraphs: MeaningLangText[];
  processSteps: MeaningLangText[];
  registrationParagraph: MeaningLangText;
  sponsor: MeaningLangText;
}

export interface MeaningOfficialContact {
  id: 'xiaohongshu' | 'douyin';
  label: MeaningLangText;
  qrImageSrc: string;
  profileUrl: string;
  handleLine: MeaningLangText;
}

/** 问卷 URL（篇章首页与活动说明页共用） */
export const MEANING_SURVEY_URL = 'https://v.wjx.cn/vm/tz1jzfM.aspx#';

/**
 * 活动说明页 · 直联报名（沪粤）
 * - 将小红书二维码 PNG 放入 `frontend/public/...` 后填写 `qrImageSrc`
 * - `profileUrl` 可选：点击「打开小红书主页」跳转
 */
export const meaningRecruitDirectSignup = {
  qrImageSrc: '' as string,
  profileUrl: '' as string,
  handleLine: { zh: '小红书账号（请在 meaningData 中填写）', en: 'Xiaohongshu handle (set in meaningData.ts)' },
};

/**
 * 官方账号入口（路书页 + 关于页共用）
 * - 将二维码 PNG 放入 `frontend/public/...` 后填写 `qrImageSrc`
 * - `profileUrl` 可选：用于跳转对应主页
 */
export const meaningOfficialContacts: MeaningOfficialContact[] = [
  {
    id: 'xiaohongshu',
    label: { zh: '小红书', en: 'Xiaohongshu' },
    qrImageSrc: 'https://images.cameradrift.com/photos/QRcode/%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_2026-04-26_004635_876.png',
    profileUrl: '',
    handleLine: { zh: '安宁｜小红书号：7527914100', en: 'Anning | Xiaohongshu ID: 7527914100' },
  },
  {
    id: 'douyin',
    label: { zh: '抖音', en: 'Douyin' },
    qrImageSrc: 'https://images.cameradrift.com/photos/QRcode/%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_2026-04-26_003912_815.png',
    profileUrl: '',
    handleLine: { zh: '安宁_｜抖音号：68312632891', en: 'Anning_ | Douyin ID: 68312632891' },
  },
];

export const meaningRecruitContent: MeaningRecruitContent = {
  title: { zh: '胶片漂流 ·「旅行的意义」篇章', en: 'Film Drift · “The Meaning of Travel” Chapter' },
  subtitle: { zh: '路书与招募', en: 'Playbook & how to join' },
  narrativeParagraphs: [
    {
      zh: '旅行归来以后，人常常会站在原地发问：这一路，对我究竟意味着什么？',
      en: 'After a journey ends, we often stand still and ask: what did this road mean to me?'
    },
    {
      zh: '未必是打卡式的名胜，也未必张张锐利清晰；更多时候，是风掠过发梢的触感、偶然抬头的天色、同行人的侧脸，与车窗里一掠而过的街景。',
      en: 'Not always famous checkpoints or razor-sharp frames—more often, wind at your hair, a sky you did not plan, the profile beside you, and a street scene half-seen from a window.'
    },
    {
      zh: '「旅行的意义」是一条按月延展的胶片支线：每期寄出一次性相机，你在路上诚实按下快门；除胶片外，也欢迎文字、静照与短片与旅途互文。寄回后统一冲洗与编排，汇成当月成册。',
      en: '“The Meaning of Travel” is a monthly film thread: each round we mail a disposable camera—you press the shutter honestly on the road. Beyond film, words, stills, and short clips may answer the journey. After return, we develop and curate into that month’s chapter.'
    },
  ],
  processSteps: [
    {
      zh: '寄来一卷光：确认同行后，一次性相机抵达掌心，像一封未拆的信。',
      en: 'A roll arrives: once you join, a disposable camera reaches your hands like an unopened letter.'
    },
    {
      zh: '把路途折进快门：海、山、或陌生街巷，只取那些心里一动的瞬间。',
      en: 'Fold the road into the shutter—sea, hills, or unfamiliar streets—only the instants that quietly insist.'
    },
    {
      zh: '以文字与影像互文：胶片之外，亦可用字句、静照或短片与旅途对谈。',
      en: 'Let words answer images: beyond film, lines, stills, or short clips may speak back to the journey.'
    },
    {
      zh: '送回暗房与册页：底片归位冲洗，再汇成当月《旅行的意义》一卷。',
      en: 'Return to the darkroom and the page: negatives come home for developing, then gather into this month’s chapter.'
    }
  ],
  registrationParagraph: {
    zh: '若你正预备一场出发，愿以一卷胶片把脚步放慢：在沪粤可直联小红书报名；其他城市请填写问卷，便于我们统一排期与寄送。',
    en: 'If you are about to leave and would slow down with one roll: in Shanghai or Guangdong, reach us on Xiaohongshu; elsewhere, use the survey so we can schedule and ship cleanly.'
  },
  sponsor: { zh: '承蒙「换个研究」襄助胶片之器。', en: 'With gratitude to “Another Research” for the cameras.' }
};

export interface MeaningMonth {
  id: string; // e.g. 2026-04
  stage: MeaningMonthStage; // 当前阶段（用于页面主 CTA / 显示不同模块）

  navLabel: MeaningLangText; // 月份标签：4月/April

  /**
   * 月详情页专用文案（不重复招募页长文）
   * 篇章首页 / 招募页写统一规则；这里只写「这个月在看什么」
   */
  monthPage: {
    chip: MeaningLangText;
    title: MeaningLangText;
    subline: MeaningLangText;
    /** 归档卡片上的一句话预告（替代全文标题） */
    cardTeaser: MeaningLangText;
    /** 可选：覆盖月页「同行者」导语（不写则用 i18n 默认） */
    participantsIntroOverride?: MeaningLangText;
  };

  hero: MeaningHero;
  sponsor: MeaningLangText & { linkUrl?: string };

  // 招募文案（“旅行的意义”这条主线）
  intro: {
    lead: MeaningLangText; // 背景小标题（如“4月招募正式开启”）
    title: MeaningLangText; // 如“胶片漂流 ·「旅行的意义」篇章”
    narrativeParagraphs: MeaningLangText[]; // 2-3 段主叙事
    registrationParagraph: MeaningLangText; // “立即报名”引导
  };

  // 参与方式（四步）
  processSteps: MeaningLangText[]; // 领取/记录/多维创作/共同冲洗

  // 立即报名链接（招募期 CTA 用）
  surveyUrl?: string;

  // 阶段化展示（可选：冲洗中/已发布）
  stageTimeline: Array<{
    stage: MeaningMonthStage;
    // 用于页面展示：例如“4月01日-4月30日”
    rangeLabel?: MeaningLangText;
  }>;

  // 进行中动态墙（可选）
  moments?: MeaningMoment[];

  // 成果展（可选）
  showcase?: {
    films?: MeaningFilmPack[];
    videos?: MeaningShowcaseVideo[];
    messages?: MeaningShowcaseMessage[];
  };

  // 每月参与者（核心变化层）
  members: MeaningMember[];

  /** 尚无成员时，展示「胶片格」占位数量（如三条路对应三格） */
  participantSlotCount?: number;
}

export type MeaningTimelineState = 'current' | 'next' | 'past';

const APRIL_MONTH_ID = '2026-04';

const aprilRollFrames = {
  'roll-a': ['000001', '000002', '000003', '000004', '000005', '000006', '000007', '000008', '000009', '000010', '000011', '000015', '000016', '000017'],
  'roll-b': ['000001', '000002', '000003', '000004', '000005', '000006', '000018', '000019', '000020', '000021', '000022', '000023', '000024', '000025', '000026', '000027', '000028'],
  'roll-c': ['000001', '000002', '000003', '000004', '000005', '000006', '000018', '000019', '000020', '000021', '000022', '000023', '000024', '000025', '000026', '000027', '000028'],
  'roll-d': ['000001', '000002', '000003', '000004', '000005', '000006', '000015', '000016', '000017', '000018', '000019', '000020', '000021', '000022'],
} as const;

type AprilRollId = keyof typeof aprilRollFrames;

const aprilRollLabels: Record<AprilRollId, MeaningLangText> = {
  'roll-a': { zh: 'A卷', en: 'Roll A' },
  'roll-b': { zh: 'B卷', en: 'Roll B' },
  'roll-c': { zh: 'C卷', en: 'Roll C' },
  'roll-d': { zh: 'D卷', en: 'Roll D' },
};

const aprilMediaPath = (rollId: AprilRollId, section: 'photos' | 'message' | 'videos', fileName: string) =>
  `/photos/meaning/${APRIL_MONTH_ID}/${rollId}/${section}/${fileName}`;

const aprilPhotoAsset = (rollId: AprilRollId, frame: string, section: 'photos' | 'message' = 'photos') =>
  createRemoteImageAsset(aprilMediaPath(rollId, section, `${frame}.jpg`));

const createAprilPhotoMoment = (
  rollId: AprilRollId,
  frame: string,
  tags: string[] = []
): MeaningPhotoMoment => {
  const label = aprilRollLabels[rollId];
  const frameNumber = Number(frame);

  return {
    id: `april-${rollId}-photo-${frame}`,
    type: 'photo',
    tags: ['四月', '胶片', label.zh, ...tags],
    source: { zh: `4月 · ${label.zh}`, en: `April · ${label.en}` },
    photo: aprilPhotoAsset(rollId, frame),
    zhCaption: `${label.zh} · 第 ${frameNumber} 格`,
    enCaption: `${label.en} · Frame ${frameNumber}`,
  };
};

const createAprilRollPhotos = (rollId: AprilRollId): MeaningPhotoMoment[] =>
  aprilRollFrames[rollId].map((frame) => createAprilPhotoMoment(rollId, frame));

const aprilRollCMessage: MeaningPhotoMoment = {
  id: 'april-roll-c-message-1',
  type: 'photo',
  tags: ['四月', '留言', '文字'],
  source: { zh: '4月 · C卷', en: 'April · Roll C' },
  photo: aprilPhotoAsset('roll-c', '000001', 'message'),
  zhCaption: 'C卷 · 文字记录',
  enCaption: 'Roll C · Written note',
};

const aprilRollCVideos: MeaningVideoMoment[] = [
  {
    id: 'april-roll-c-video-1',
    type: 'video',
    tags: ['四月', '视频', '旅途'],
    source: { zh: '4月 · C卷', en: 'April · Roll C' },
    videoSources: [
      {
        kind: 'selfHosted',
        selfHosted: {
          src: aprilMediaPath('roll-c', 'videos', '000001.mp4'),
          poster: aprilMediaPath('roll-c', 'photos', '000001.jpg'),
        },
      },
    ],
  },
  {
    id: 'april-roll-c-video-2',
    type: 'video',
    tags: ['四月', '视频', '旅途'],
    source: { zh: '4月 · C卷', en: 'April · Roll C' },
    videoSources: [
      {
        kind: 'selfHosted',
        selfHosted: {
          src: aprilMediaPath('roll-c', 'videos', '000002.mp4'),
          poster: aprilMediaPath('roll-c', 'photos', '000002.jpg'),
        },
      },
    ],
  },
];

const createAprilRollMember = (
  rollId: AprilRollId,
  keyword: MeaningLangText,
  tagline: MeaningLangText,
  extraSubmissions: MeaningMoment[] = []
): MeaningMember => ({
  id: rollId,
  name: aprilRollLabels[rollId].zh,
  city: '四月篇章',
  keyword,
  tagline,
  status: 'published',
  submissions: [
    ...createAprilRollPhotos(rollId),
    ...extraSubmissions,
  ],
});

/** 2026-04：四卷影像已归档 */
const aprilCityLanes: MeaningMember[] = [
  createAprilRollMember(
    'roll-a',
    { zh: '第一卷路途里的光与停顿', en: 'Light and pauses from the first roll' },
    { zh: 'A卷收下四月最先显影的一组画格，像旅途刚刚打开时的呼吸。', en: 'Roll A keeps the first frames of April, like the first breath of a journey opening.' }
  ),
  createAprilRollMember(
    'roll-b',
    { zh: '陌生街巷与慢下来的时间', en: 'Unfamiliar streets and softened time' },
    { zh: 'B卷把街巷、树影与路上的间隙留在同一段胶片里。', en: 'Roll B keeps lanes, tree shadows, and the pauses between places on one strip of film.' }
  ),
  createAprilRollMember(
    'roll-c',
    { zh: '影像、文字与短片互文', en: 'Images, notes, and short clips in dialogue' },
    { zh: 'C卷除了照片，也带回一页文字和两段短片，让路途有了声音。', en: 'Roll C returns with photographs, one written note, and two short clips, giving the road a voice.' },
    [aprilRollCMessage, ...aprilRollCVideos]
  ),
  createAprilRollMember(
    'roll-d',
    { zh: '抵达之前的余光', en: 'Afterglow before arrival' },
    { zh: 'D卷像旅程末尾仍未散去的余光，把四月慢慢收束。', en: 'Roll D gathers April like afterglow that lingers before arrival.' }
  ),
];

const aprilHubMoments: MeaningMoment[] = [
  createAprilPhotoMoment('roll-a', '000001', ['风景']),
  createAprilPhotoMoment('roll-b', '000001', ['街巷']),
  createAprilPhotoMoment('roll-c', '000001', ['路上']),
  aprilRollCMessage,
  aprilRollCVideos[0],
  createAprilPhotoMoment('roll-d', '000001', ['余光']),
];

const MAY_MONTH_ID = '2026-05';
const JUNE_MONTH_ID = '2026-06';

type MeaningArchiveSection = 'photos' | 'message' | 'videos';

const stripFileExtension = (fileName: string) => fileName.replace(/\.[^.]+$/, '');

const normalizeMomentIdPart = (value: string) =>
  value
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const createMeaningArchiveMediaPath = (
  monthId: string,
  rollId: string,
  section: MeaningArchiveSection,
  fileName: string
) => `/photos/meaning/${monthId}/${rollId}/${section}/${fileName}`;

const createMeaningArchivePhotoAsset = (
  monthId: string,
  rollId: string,
  fileName: string,
  section: 'photos' | 'message' = 'photos'
) => createRemoteImageAsset(createMeaningArchiveMediaPath(monthId, rollId, section, fileName));

const createMeaningArchivePhotoMoment = <RollId extends string>(
  monthSlug: string,
  monthId: string,
  monthTag: string,
  monthSource: MeaningLangText,
  rollLabels: Record<RollId, MeaningLangText>,
  rollId: RollId,
  fileName: string,
  section: 'photos' | 'message' = 'photos',
  tags: string[] = []
): MeaningPhotoMoment => {
  const label = rollLabels[rollId];
  const isMessage = section === 'message';
  const frameLabel = stripFileExtension(fileName);
  const idPart = normalizeMomentIdPart(fileName);

  return {
    id: `${monthSlug}-${rollId}-${section}-${idPart}`,
    type: 'photo',
    tags: [monthTag, isMessage ? '留言' : '胶片', label.zh, ...tags],
    source: { zh: `${monthSource.zh} · ${label.zh}`, en: `${monthSource.en} · ${label.en}` },
    photo: createMeaningArchivePhotoAsset(monthId, rollId, fileName, section),
    zhCaption: isMessage ? `${label.zh} · 文字记录` : `${label.zh} · 第 ${frameLabel} 格`,
    enCaption: isMessage ? `${label.en} · Written note` : `${label.en} · Frame ${frameLabel}`,
  };
};

const createMeaningArchiveRollMember = <RollId extends string>(
  rollId: RollId,
  rollLabels: Record<RollId, MeaningLangText>,
  city: string,
  keyword: MeaningLangText,
  tagline: MeaningLangText,
  photos: MeaningPhotoMoment[],
  extraSubmissions: MeaningMoment[] = []
): MeaningMember => ({
  id: rollId,
  name: rollLabels[rollId].zh,
  city,
  keyword,
  tagline,
  status: 'published',
  submissions: [
    ...photos,
    ...extraSubmissions,
  ],
});

const mayRollPhotoFiles = {
  'roll-a': ['000001.jpg', '000002.jpg', '000003.jpg', '000004.jpg', '000005.jpg', '000006.jpg', '000029.jpg', '000030.jpg', '000031.jpg', '000032.jpg', '000033.jpg', '000034.jpg', '000035.jpg', '000036.jpg', '000037.jpg', '000038.jpg', '000039.jpg', '000040.jpg', '000041.jpg', '000042.jpg', '000043.jpg', '000044.jpg', '000045.jpg', '000046.jpg', '000047.jpg', '000048.jpg', '000049.jpg', '000050.jpg'],
  'roll-b': ['000002.jpg', '000003.jpg', '000004.jpg', '000005.jpg', '000006.jpg', '000030.jpg', '000031.jpg', '000032.jpg', '000033.jpg', '000034.jpg', '000035.jpg', '000036.jpg', '000037.jpg', '000038.jpg', '000039.jpg', '000040.jpg', '000041.jpg', '000042.jpg', '000043.jpg', '000044.jpg', '000045.jpg', '000046.jpg', '000047.jpg', '000048.jpg', '000049.jpg', '000050.jpg', '000051.jpg', '000052.jpg'],
} as const;

type MayRollId = keyof typeof mayRollPhotoFiles;

const mayRollLabels: Record<MayRollId, MeaningLangText> = {
  'roll-a': { zh: 'A卷', en: 'Roll A' },
  'roll-b': { zh: 'B卷', en: 'Roll B' },
};

const mayRollIds = ['roll-a', 'roll-b'] as const;
const maySource = { zh: '5月', en: 'May' };

const mayPhotoAsset = (rollId: MayRollId, fileName: string, section: 'photos' | 'message' = 'photos') =>
  createMeaningArchivePhotoAsset(MAY_MONTH_ID, rollId, fileName, section);

const createMayPhotoMoment = (rollId: MayRollId, fileName: string, tags: string[] = []) =>
  createMeaningArchivePhotoMoment('may', MAY_MONTH_ID, '五月', maySource, mayRollLabels, rollId, fileName, 'photos', tags);

const createMayRollPhotos = (rollId: MayRollId): MeaningPhotoMoment[] =>
  mayRollPhotoFiles[rollId].map((fileName) => createMayPhotoMoment(rollId, fileName));

const mayRollMembers: MeaningMember[] = [
  createMeaningArchiveRollMember(
    'roll-a',
    mayRollLabels,
    '五月篇章',
    { zh: '五月第一卷的光与路', en: 'Light and roads from May Roll A' },
    { zh: 'A卷把五月旅途中先显影的风景、停顿与偶遇收进同一卷里。', en: 'Roll A gathers May’s early frames: landscapes, pauses, and small encounters on the road.' },
    createMayRollPhotos('roll-a')
  ),
  createMeaningArchiveRollMember(
    'roll-b',
    mayRollLabels,
    '五月篇章',
    { zh: '另一段旅途里的慢镜头', en: 'Slow frames from another May route' },
    { zh: 'B卷继续把五月的脚步放慢，让街巷、远处的光和路上的片刻慢慢展开。', en: 'Roll B slows May down again, opening lanes, distant light, and passing moments frame by frame.' },
    createMayRollPhotos('roll-b')
  ),
];

const mayHubMoments: MeaningMoment[] = [
  createMayPhotoMoment('roll-a', mayRollPhotoFiles['roll-a'][0], ['风景']),
  createMayPhotoMoment('roll-b', mayRollPhotoFiles['roll-b'][0], ['路上']),
];

const mayFilmPacks: MeaningFilmPack[] = mayRollIds.map((rollId) => ({
  id: `may-${rollId}-film`,
  title: mayRollLabels[rollId],
  photos: mayRollPhotoFiles[rollId].map((fileName) => mayPhotoAsset(rollId, fileName)),
}));

const juneRollPhotoFiles = {
  'roll-a': ['000001.JPG', '000002.JPG', '000003.JPG', '000004.JPG', '000005.JPG', '000006.JPG', '000010.JPG', '000011.JPG', '000012.JPG', '000033.JPG', '000034.JPG', '000035.JPG', '000036.JPG'],
  'roll-b': ['000013.JPG', '000014.JPG', '000015.JPG', '000016.JPG', '000017.JPG', '000018.JPG', '000028.JPG', '000029.JPG', '000030.JPG', '000031.JPG', '000032.JPG'],
  'roll-c': ['000001.JPG', '000002.JPG', '000003.JPG', '000004.JPG', '000005.JPG', '000006.JPG', '000028.JPG', '000029.JPG', '000030.JPG', '000031.JPG', '000032.JPG', '000033.JPG', '000034.JPG', '000035.JPG', '000036.JPG', '000037.JPG', '000038.JPG', '000039.JPG', '000040.JPG', '000041.JPG', '000042.JPG', '000043.JPG', '000044.JPG', '000045.JPG', '000046.JPG', '000047.JPG', '000048.JPG'],
} as const;

const juneRollMessageFiles = {
  'roll-a': ['微信图片_20260809202304_516_2.jpg'],
  'roll-b': ['微信图片_20260809202304_516_2.jpg'],
  'roll-c': ['微信图片_2026-08-09_202642_339.png', '微信图片_2026-08-09_202658_245.png', '微信图片_2026-08-09_202708_326.png', '微信图片_2026-08-09_202717_678.png', '微信图片_2026-08-09_202726_007.png', '微信图片_2026-08-09_202734_733.png', '微信图片_2026-08-09_202744_090.png'],
} as const;

type JuneRollId = keyof typeof juneRollPhotoFiles;

const juneRollLabels: Record<JuneRollId, MeaningLangText> = {
  'roll-a': { zh: 'A卷', en: 'Roll A' },
  'roll-b': { zh: 'B卷', en: 'Roll B' },
  'roll-c': { zh: 'C卷', en: 'Roll C' },
};

const juneRollIds = ['roll-a', 'roll-b', 'roll-c'] as const;
const juneSource = { zh: '6月', en: 'June' };

const junePhotoAsset = (rollId: JuneRollId, fileName: string, section: 'photos' | 'message' = 'photos') =>
  createMeaningArchivePhotoAsset(JUNE_MONTH_ID, rollId, fileName, section);

const createJunePhotoMoment = (rollId: JuneRollId, fileName: string, tags: string[] = []) =>
  createMeaningArchivePhotoMoment('june', JUNE_MONTH_ID, '六月', juneSource, juneRollLabels, rollId, fileName, 'photos', tags);

const createJuneMessageMoment = (rollId: JuneRollId, fileName: string, tags: string[] = []) =>
  createMeaningArchivePhotoMoment('june', JUNE_MONTH_ID, '六月', juneSource, juneRollLabels, rollId, fileName, 'message', ['文字', ...tags]);

const createJuneRollPhotos = (rollId: JuneRollId): MeaningPhotoMoment[] =>
  juneRollPhotoFiles[rollId].map((fileName) => createJunePhotoMoment(rollId, fileName));

const juneMessageMoments: Record<JuneRollId, MeaningPhotoMoment[]> = {
  'roll-a': juneRollMessageFiles['roll-a'].map((fileName) => createJuneMessageMoment('roll-a', fileName)),
  'roll-b': juneRollMessageFiles['roll-b'].map((fileName) => createJuneMessageMoment('roll-b', fileName)),
  'roll-c': juneRollMessageFiles['roll-c'].map((fileName) => createJuneMessageMoment('roll-c', fileName)),
};

const juneRollMembers: MeaningMember[] = [
  createMeaningArchiveRollMember(
    'roll-a',
    juneRollLabels,
    '六月篇章',
    { zh: '六月第一卷的出发', en: 'The first departure of June' },
    { zh: 'A卷从六月的起点出发，把天气、路途与那些偶然停下来的瞬间带回。', en: 'Roll A starts June with weather, roads, and the moments that made someone pause.' },
    createJuneRollPhotos('roll-a'),
    juneMessageMoments['roll-a']
  ),
  createMeaningArchiveRollMember(
    'roll-b',
    juneRollLabels,
    '六月篇章',
    { zh: '换一条路看见六月', en: 'Seeing June by another route' },
    { zh: 'B卷把另一条路上的光线和留言并置，让六月有了第二种语气。', en: 'Roll B pairs another route’s light with a written note, giving June a second voice.' },
    createJuneRollPhotos('roll-b'),
    juneMessageMoments['roll-b']
  ),
  createMeaningArchiveRollMember(
    'roll-c',
    juneRollLabels,
    '六月篇章',
    { zh: '照片与文字一起回声', en: 'Photographs and notes echo together' },
    { zh: 'C卷带回最多的画格与留言，像一段旅途在纸面和影像里反复回声。', en: 'Roll C returns with the most frames and notes, echoing the journey through both paper and image.' },
    createJuneRollPhotos('roll-c'),
    juneMessageMoments['roll-c']
  ),
];

const juneHubMoments: MeaningMoment[] = [
  createJunePhotoMoment('roll-a', juneRollPhotoFiles['roll-a'][0], ['风景']),
  juneMessageMoments['roll-a'][0],
  createJunePhotoMoment('roll-b', juneRollPhotoFiles['roll-b'][0], ['路上']),
  juneMessageMoments['roll-b'][0],
  createJunePhotoMoment('roll-c', juneRollPhotoFiles['roll-c'][0], ['旅途']),
  juneMessageMoments['roll-c'][0],
];

const juneFilmPacks: MeaningFilmPack[] = juneRollIds.map((rollId) => ({
  id: `june-${rollId}-film`,
  title: juneRollLabels[rollId],
  photos: juneRollPhotoFiles[rollId].map((fileName) => junePhotoAsset(rollId, fileName)),
}));

// -----------------------------
// 示例数据：当前先落地 2026-04
// -----------------------------

export const meaningMonths: MeaningMonth[] = [
  {
    id: '2026-04',
    stage: 'published',
    navLabel: { zh: '4月', en: 'April' },
    monthPage: {
      chip: { zh: '2026 · 四月企划', en: 'April 2026' },
      title: { zh: '四卷胶片，四种路途', en: 'Four rolls, four paths' },
      subline: {
        zh: '四月的画格已经归来：四卷胶片、一路上的文字与短片，在这里慢慢摊开。它们未必回答旅行的意义，却把每一次停顿、抬头和回望留了下来。',
        en:
          'April has returned: four rolls of film, notes, and short clips open here slowly. They may not answer the meaning of travel, but they keep every pause, glance upward, and look back.'
      },
      cardTeaser: {
        zh: '四卷胶片已归档：照片、文字与短片在四月的页码里显影。',
        en: 'Four rolls are archived: photographs, notes, and short clips developing across April.'
      },
      participantsIntroOverride: {
        zh: '四月篇章按四卷归档；点击任一卷，可以进入对应的照片、留言与视频记录。',
        en: 'April is archived as four rolls. Open any roll to view its photographs, notes, and video records.'
      }
    },
    hero: {
      heroVideoSources: [],
      posterImage: aprilPhotoAsset('roll-a', '000001'),
    },
    sponsor: {
      zh: '换个研究',
      en: 'Another Research',
      linkUrl: undefined,
    },
    intro: {
      title: meaningRecruitContent.title,
      lead: meaningRecruitContent.subtitle,
      narrativeParagraphs: meaningRecruitContent.narrativeParagraphs,
      registrationParagraph: meaningRecruitContent.registrationParagraph,
    },
    processSteps: meaningRecruitContent.processSteps,
    surveyUrl: undefined,
    stageTimeline: [
      { stage: 'recruiting', rangeLabel: { zh: '4月招募', en: 'Recruitment in April' } },
      { stage: 'inProgress', rangeLabel: { zh: '旅程记录进行中', en: 'Journey submissions in progress' } },
      { stage: 'developing', rangeLabel: { zh: '冲洗与整理', en: 'Developing & curating' } },
      { stage: 'published', rangeLabel: { zh: '成果展发布', en: 'Showcase published' } },
    ],
    moments: aprilHubMoments,
    showcase: {
      films: [
        {
          id: 'april-roll-a-film',
          title: { zh: 'A卷', en: 'Roll A' },
          photos: aprilRollFrames['roll-a'].map((frame) => aprilPhotoAsset('roll-a', frame)),
        },
        {
          id: 'april-roll-b-film',
          title: { zh: 'B卷', en: 'Roll B' },
          photos: aprilRollFrames['roll-b'].map((frame) => aprilPhotoAsset('roll-b', frame)),
        },
        {
          id: 'april-roll-c-film',
          title: { zh: 'C卷', en: 'Roll C' },
          photos: aprilRollFrames['roll-c'].map((frame) => aprilPhotoAsset('roll-c', frame)),
        },
        {
          id: 'april-roll-d-film',
          title: { zh: 'D卷', en: 'Roll D' },
          photos: aprilRollFrames['roll-d'].map((frame) => aprilPhotoAsset('roll-d', frame)),
        },
      ],
      videos: [
        {
          id: 'april-roll-c-videos',
          title: { zh: 'C卷短片', en: 'Roll C Clips' },
          sources: aprilRollCVideos.flatMap((video) => video.videoSources),
        },
      ],
      messages: [
        {
          id: 'april-roll-c-note',
          content: {
            zh: 'C卷带回了一页文字记录，已和照片一起归入本月档案。',
            en: 'Roll C returned with a written note, archived with this month’s photographs.',
          },
        },
      ],
    },
    members: aprilCityLanes
  },
  {
    id: '2026-05',
    stage: 'published',
    navLabel: { zh: '5月', en: 'May' },
    monthPage: {
      chip: { zh: '2026 · 五月归档', en: 'May 2026 Archive' },
      title: { zh: '两卷胶片，五月的两段路', en: 'Two rolls, two May routes' },
      subline: {
        zh: '五月的两卷胶片已经归来：它们把不同路途里的光、街巷、停顿与回望放在一起，慢慢构成这一月的册页。',
        en: 'May has returned in two rolls: light, lanes, pauses, and backward glances from different routes gather into this month’s chapter.'
      },
      cardTeaser: {
        zh: '五月两卷已归档：照片在两段路上慢慢显影。',
        en: 'Two May rolls are archived, developing across two routes.'
      },
      participantsIntroOverride: {
        zh: '五月篇章按两卷归档；点击任一卷，可以进入对应的照片记录。',
        en: 'May is archived as two rolls. Open either roll to view its photographs.'
      }
    },
    hero: {
      heroVideoSources: [],
      posterImage: mayPhotoAsset('roll-a', mayRollPhotoFiles['roll-a'][0]),
    },
    sponsor: {
      zh: '换个研究',
      en: 'Another Research',
      linkUrl: undefined,
    },
    intro: {
      title: meaningRecruitContent.title,
      lead: { zh: '五月成果展发布', en: 'May showcase published' },
      narrativeParagraphs: meaningRecruitContent.narrativeParagraphs,
      registrationParagraph: meaningRecruitContent.registrationParagraph,
    },
    processSteps: meaningRecruitContent.processSteps,
    surveyUrl: undefined,
    stageTimeline: [
      { stage: 'recruiting', rangeLabel: { zh: '5月招募', en: 'Recruitment in May' } },
      { stage: 'inProgress', rangeLabel: { zh: '旅程记录进行中', en: 'Journey submissions in progress' } },
      { stage: 'developing', rangeLabel: { zh: '冲洗与整理', en: 'Developing & curating' } },
      { stage: 'published', rangeLabel: { zh: '成果展发布', en: 'Showcase published' } },
    ],
    moments: mayHubMoments,
    showcase: {
      films: mayFilmPacks,
      videos: [],
      messages: [],
    },
    members: mayRollMembers
  },
  {
    id: '2026-06',
    stage: 'published',
    navLabel: { zh: '6月', en: 'June' },
    monthPage: {
      chip: { zh: '2026 · 六月归档', en: 'June 2026 Archive' },
      title: { zh: '三卷胶片，六月的回声', en: 'Three rolls, June’s echoes' },
      subline: {
        zh: '六月的三卷胶片和留言已经归来：照片记录路上的光，文字把那一刻的心绪留在旁边。',
        en: 'June returns with three rolls and written notes: photographs keep the light of the road, while words stay beside those moments.'
      },
      cardTeaser: {
        zh: '六月三卷已归档：照片与留言一起显影。',
        en: 'Three June rolls are archived, with photographs and notes developing together.'
      },
      participantsIntroOverride: {
        zh: '六月篇章按三卷归档；点击任一卷，可以进入对应的照片与留言记录。',
        en: 'June is archived as three rolls. Open any roll to view its photographs and notes.'
      }
    },
    hero: {
      heroVideoSources: [],
      posterImage: junePhotoAsset('roll-a', juneRollPhotoFiles['roll-a'][0]),
    },
    sponsor: {
      zh: '换个研究',
      en: 'Another Research',
      linkUrl: undefined,
    },
    intro: {
      title: meaningRecruitContent.title,
      lead: { zh: '六月成果展发布', en: 'June showcase published' },
      narrativeParagraphs: meaningRecruitContent.narrativeParagraphs,
      registrationParagraph: meaningRecruitContent.registrationParagraph,
    },
    processSteps: meaningRecruitContent.processSteps,
    surveyUrl: undefined,
    stageTimeline: [
      { stage: 'recruiting', rangeLabel: { zh: '6月招募', en: 'Recruitment in June' } },
      { stage: 'inProgress', rangeLabel: { zh: '旅程记录进行中', en: 'Journey submissions in progress' } },
      { stage: 'developing', rangeLabel: { zh: '冲洗与整理', en: 'Developing & curating' } },
      { stage: 'published', rangeLabel: { zh: '成果展发布', en: 'Showcase published' } },
    ],
    moments: juneHubMoments,
    showcase: {
      films: juneFilmPacks,
      videos: [],
      messages: [
        {
          id: 'june-roll-a-note',
          content: {
            zh: 'A卷带回了一页留言，和第一段六月路途一起归档。',
            en: 'Roll A returned with one note, archived with the first June route.',
          },
        },
        {
          id: 'june-roll-b-note',
          content: {
            zh: 'B卷也留下了一页文字，让照片之外的心绪有了位置。',
            en: 'Roll B also keeps a written page, giving the feelings beyond the frames a place.',
          },
        },
        {
          id: 'june-roll-c-notes',
          content: {
            zh: `C卷带回 ${juneMessageMoments['roll-c'].length} 页留言，已和照片一起归入六月档案。`,
            en: `Roll C returned with ${juneMessageMoments['roll-c'].length} written notes, archived with June’s photographs.`,
          },
        },
      ],
    },
    members: juneRollMembers
  },
  {
    id: '2026-07-08',
    stage: 'recruiting',
    navLabel: { zh: '7/8月', en: 'July/August' },
    monthPage: {
      chip: { zh: '2026 · 七八月招募', en: 'July/August 2026 Recruitment' },
      title: { zh: '把盛夏交给下一段路', en: 'Let midsummer belong to the next road' },
      subline: {
        zh: '七月没有单独开启，于是把这一次出发留给七八月共同完成。若你正准备一段夏末以前的路，可以把相机交给风、车窗、海面、山线，或任何让你愿意停下来的瞬间。',
        en: 'July did not open as a separate round, so this departure is shared by July and August. If a road is waiting before summer fades, let the camera belong to wind, windows, sea, ridgelines, or any moment that makes you pause.'
      },
      cardTeaser: {
        zh: '七八月联合招募开启：把盛夏与夏末之间的路途，留给下一卷胶片。',
        en: 'July/August recruitment is open: let the road between midsummer and late summer become the next roll.'
      },
      participantsIntroOverride: {
        zh: '七八月作为一期联合招募；新的相机正在等待旅伴，把盛夏路上的画格带回来。',
        en: 'July and August are recruited as one combined round. New cameras are waiting for travelers to bring back frames from summer roads.'
      }
    },
    hero: {
      heroVideoSources: [],
      posterImage: undefined,
    },
    sponsor: {
      zh: '换个研究',
      en: 'Another Research',
      linkUrl: undefined,
    },
    intro: {
      title: meaningRecruitContent.title,
      lead: { zh: '七八月联合招募开启', en: 'July/August recruitment open' },
      narrativeParagraphs: meaningRecruitContent.narrativeParagraphs,
      registrationParagraph: meaningRecruitContent.registrationParagraph,
    },
    processSteps: meaningRecruitContent.processSteps,
    surveyUrl: undefined,
    stageTimeline: [
      { stage: 'recruiting', rangeLabel: { zh: '7/8月联合招募', en: 'July/August recruitment' } },
      { stage: 'inProgress', rangeLabel: { zh: '旅程记录进行中', en: 'Journey submissions in progress' } },
      { stage: 'developing', rangeLabel: { zh: '冲洗与整理', en: 'Developing & curating' } },
      { stage: 'published', rangeLabel: { zh: '成果展发布', en: 'Showcase published' } },
    ],
    moments: [],
    showcase: {
      films: [],
      videos: [],
      messages: [],
    },
    members: [],
    participantSlotCount: 4
  },
];

export const getMeaningMonths = (): MeaningMonth[] => meaningMonths;

export const getMeaningMonthById = (monthId: string): MeaningMonth | undefined =>
  meaningMonths.find((m) => m.id === monthId);

export const getMeaningMemberById = (monthId: string, memberId: string): MeaningMember | undefined => {
  const month = getMeaningMonthById(monthId);
  return month?.members.find((m) => m.id === memberId);
};

export const getMeaningTimelineState = (month: MeaningMonth): MeaningTimelineState => {
  if (month.stage === 'published') return 'past';
  if (month.stage === 'recruiting') return 'next';
  return 'current';
};

